/**
 * Message request analytics (PPL-432) — the CLIENT half of the contract.
 *
 * Four events live here and only here:
 *
 *   dm_request_prompt_shown {}
 *   dm_request_accepted     {source}
 *   dm_request_declined     {source}
 *   dm_request_blocked      {source}
 *
 * The other four (dm_request_sent, dm_request_refused, dm_email_sent,
 * dm_email_suppressed) belong to the server and are never emitted from a
 * browser. The split is deliberate: one accept is counted once, by whichever
 * half actually saw it happen.
 *
 * `source` is the surface that answered, and it is the whole payload:
 *
 *   'prompt'  — the app-wide request prompt (App.jsx mounts one)
 *   'inbox'   — the same card re-opened from the requests section
 *   'profile' — the pending-request bar on /user/:id
 *   'thread'  — the accept/decline banner inside the conversation
 *
 * PRIVACY. DMs are the anonymous half, so these events carry no handle, no
 * name, no message text, no chat id and no uid of the other party — enum
 * members and nothing else. The chat ids below are used for de-duplication in
 * memory; none of them is ever a property.
 *
 * There is no `platform` property here, unlike homeAnalytics: this contract is
 * shared verbatim with the server and the app, so web must not add a field the
 * other two do not send. PostHog's own $lib already separates posthog-js from
 * the app SDK and from posthog-node.
 *
 * Everything goes through the guarded `capture` in services/posthog: no key,
 * no throw, never in the way of a user flow.
 */

import { capture } from '../services/posthog';
import { hasBeenPrompted } from '../hooks/useMessageRequests';

export const MessageRequestEvents = {
  PROMPT_SHOWN: 'dm_request_prompt_shown',
  ACCEPTED: 'dm_request_accepted',
  DECLINED: 'dm_request_declined',
  BLOCKED: 'dm_request_blocked',
} as const;

/** Which surface the answer came from. */
export type RequestSource = 'prompt' | 'inbox' | 'profile' | 'thread';

/** What was done to the request. `block` also answers it, on the card. */
export type RequestAnswer = 'accept' | 'decline' | 'block';

/**
 * Chat ids already counted as shown, for the lifetime of this tab.
 *
 * This is a guard of its own rather than the `prompted` set in
 * useMessageRequests, because that set answers a different question. It is
 * read BEFORE a request is shown (the prompt host filters on it), so at the
 * moment the card renders it always says "not yet" — it cannot dedupe React
 * 18 StrictMode's double-invoked mount effect, a remount, or the same request
 * re-opened from the inbox in this session. It is also written by paths where
 * no prompt was ever rendered (the conversation banner and the profile bar
 * both mark a request prompted when they answer it), so it does not mean
 * "this was shown".
 *
 * A module-level Set does mean exactly that: it survives unmount, StrictMode,
 * and the poller's 30-second re-render, and it is shared by both hosts of the
 * card. `hasBeenPrompted` is then consulted as the cross-reload backstop —
 * every path that marks a request prompted either follows a show or retires
 * the request, so a request already in it has been counted on this device.
 */
const counted = new Set<string>();

/** One event the first time a request's card is put in front of someone. */
export function trackRequestPromptShown(chatId: string): void {
  if (!chatId || counted.has(chatId)) return;
  counted.add(chatId);
  // Reload + re-open from the inbox: already counted on this device.
  if (hasBeenPrompted(chatId)) return;
  capture(MessageRequestEvents.PROMPT_SHOWN);
}

/**
 * The answer, once it is the server's answer and not just a pressed button.
 * Callers pass the status the server came back with, so a request answered on
 * another device inside the poll window is counted the way it actually went —
 * and, via `changed`, is not counted a second time.
 */
export function trackRequestAnswered(answer: RequestAnswer, source: RequestSource): void {
  const event =
    answer === 'accept'
      ? MessageRequestEvents.ACCEPTED
      : answer === 'decline'
        ? MessageRequestEvents.DECLINED
        : MessageRequestEvents.BLOCKED;
  capture(event, { source });
}
